import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { colors, zIndex } from './theme';

// Glass effect mixin
const glassEffect = css`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const darkGlassEffect = css`
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

// Gradient button mixin
const gradientButton = css`
  background: ${colors.primary.gradient};
  color: white;
  border: none;
  position: relative;
  overflow: hidden;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${colors.primary.gradient};
    filter: brightness(1.2);
    opacity: 0;
    z-index: -1;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

// Container with max width
export const Container = styled.div`
  width: 100%;
  max-width: ${({ maxWidth }) => maxWidth || '1200px'};
  margin: 0 auto;
  padding: 0 ${({ noPadding }) => noPadding ? '0' : '20px'};
  
  @media (max-width: 768px) {
    padding: 0 ${({ noPadding }) => noPadding ? '0' : '15px'};
  }
`;

// Animated container
export const AnimatedContainer = styled(motion.div)`
  width: 100%;
`;

// Hero section
export const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: ${({ gradient }) => gradient || `linear-gradient(135deg, ${colors.background.default} 0%, ${colors.background.paper} 100%)`};
  
  ${({ pattern }) => pattern && css`
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4a017' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
      opacity: 0.5;
    }
  `}
`;

// Card component
export const Card = styled(motion.div)`
  ${({ glass }) => glass ? glassEffect : css`
    background: ${({ theme }) => theme.mode === 'dark' ? colors.background.darkPaper : colors.background.paper};
  `};
  
  border-radius: ${({ radius }) => radius || '16px'};
  padding: ${({ padding }) => padding || '24px'};
  box-shadow: ${({ elevated }) => elevated ? '0 20px 40px rgba(0, 0, 0, 0.1)' : '0 4px 16px rgba(0, 0, 0, 0.05)'};
  transition: all 0.3s ease;
  
  ${({ hoverable }) => hoverable && css`
    cursor: pointer;
    
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 24px 48px rgba(0, 0, 0, 0.15);
    }
  `}
  
  ${({ gradient }) => gradient && css`
    background: ${gradient};
    color: white;
  `}
`;

// Glass card
export const GlassCard = styled(Card)`
  ${({ theme }) => theme.mode === 'dark' ? darkGlassEffect : glassEffect};
`;

// Gradient text
export const GradientText = styled.span`
  background: ${({ gradient }) => gradient || colors.primary.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  font-weight: ${({ weight }) => weight || '600'};
`;

// Enhanced button
export const StyledButton = styled(motion.button)`
  ${({ variant }) => variant === 'gradient' && gradientButton};
  
  padding: ${({ size }) => size === 'large' ? '16px 32px' : size === 'small' ? '8px 16px' : '12px 24px'};
  font-size: ${({ size }) => size === 'large' ? '1.125rem' : size === 'small' ? '0.875rem' : '1rem'};
  font-weight: 500;
  border-radius: ${({ rounded }) => rounded === 'full' ? '50px' : '8px'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  
  ${({ variant }) => variant === 'outlined' && css`
    background: transparent;
    color: ${colors.primary.main};
    border: 2px solid ${colors.primary.main};
    
    &:hover {
      background: ${colors.primary.main};
      color: white;
    }
  `}
  
  ${({ variant }) => variant === 'ghost' && css`
    background: transparent;
    color: ${colors.primary.main};
    border: none;
    
    &:hover {
      background: rgba(212, 160, 23, 0.1);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Sticky header
export const StickyHeader = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${zIndex.header};
  ${({ glass }) => glass && glassEffect};
  transition: all 0.3s ease;
  
  ${({ scrolled }) => scrolled && css`
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  `}
`;

// Animated sidebar
export const Sidebar = styled(motion.aside)`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: ${({ width }) => width || '280px'};
  background: ${({ theme }) => theme.mode === 'dark' ? colors.background.darkPaper : colors.background.paper};
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.1);
  z-index: ${zIndex.drawer};
  overflow-y: auto;
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 320px;
  }
`;

// Main content wrapper
export const MainContent = styled.main`
  margin-left: ${({ sidebarWidth }) => sidebarWidth || '280px'};
  min-height: 100vh;
  padding: ${({ padding }) => padding || '24px'};
  transition: margin-left 0.3s ease;
  
  @media (max-width: 768px) {
    margin-left: 0;
    padding: 16px;
  }
`;

// Grid container
export const Grid = styled.div`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || 'repeat(auto-fit, minmax(300px, 1fr))'};
  gap: ${({ gap }) => gap || '24px'};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

// Flex container
export const Flex = styled.div`
  display: flex;
  align-items: ${({ align }) => align || 'center'};
  justify-content: ${({ justify }) => justify || 'flex-start'};
  gap: ${({ gap }) => gap || '0'};
  flex-direction: ${({ direction }) => direction || 'row'};
  flex-wrap: ${({ wrap }) => wrap || 'nowrap'};
  
  ${({ responsive }) => responsive && css`
    @media (max-width: 768px) {
      flex-direction: column;
    }
  `}
`;

// Section
export const Section = styled.section`
  padding: ${({ padding }) => padding || '60px 0'};
  position: relative;
  
  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

// Badge
export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${({ variant }) => 
    variant === 'success' ? colors.success.light :
    variant === 'error' ? colors.error.light :
    variant === 'warning' ? colors.warning.light :
    variant === 'info' ? colors.info.light :
    colors.primary.light
  };
  color: ${({ variant }) => 
    variant === 'success' ? colors.success.dark :
    variant === 'error' ? colors.error.dark :
    variant === 'warning' ? colors.warning.dark :
    variant === 'info' ? colors.info.dark :
    colors.primary.dark
  };
`;

// Icon button
export const IconButton = styled(motion.button)`
  width: ${({ size }) => size === 'large' ? '48px' : size === 'small' ? '32px' : '40px'};
  height: ${({ size }) => size === 'large' ? '48px' : size === 'small' ? '32px' : '40px'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: ${({ variant }) => variant === 'contained' ? colors.primary.main : 'transparent'};
  color: ${({ variant }) => variant === 'contained' ? 'white' : colors.primary.main};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ variant }) => variant === 'contained' ? colors.primary.dark : 'rgba(212, 160, 23, 0.1)'};
    transform: scale(1.1);
  }
`;

// Divider
export const Divider = styled.hr`
  border: none;
  height: 1px;
  background: ${colors.divider};
  margin: ${({ spacing }) => spacing || '24px'} 0;
`;

// Overlay
export const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, ${({ opacity }) => opacity || 0.5});
  z-index: ${({ zIndex: z }) => z || zIndex.modal - 1};
`;

// Loading spinner
export const Spinner = styled(motion.div)`
  width: ${({ size }) => size || '40px'};
  height: ${({ size }) => size || '40px'};
  border: 3px solid ${colors.divider};
  border-top-color: ${colors.primary.main};
  border-radius: 50%;
  animation: rotate 1s linear infinite;
`;

// Tooltip
export const Tooltip = styled.div`
  position: absolute;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border-radius: 4px;
  font-size: 0.875rem;
  white-space: nowrap;
  z-index: ${zIndex.tooltip};
  pointer-events: none;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.8);
  }
`;

export default {
  Container,
  AnimatedContainer,
  HeroSection,
  Card,
  GlassCard,
  GradientText,
  StyledButton,
  StickyHeader,
  Sidebar,
  MainContent,
  Grid,
  Flex,
  Section,
  Badge,
  IconButton,
  Divider,
  Overlay,
  Spinner,
  Tooltip,
};